type Localized<T> = T extends string
	? string
	: T extends number
		? number
		: T extends boolean
			? boolean
			: T extends readonly (infer Item)[]
				? ReadonlyArray<Localized<Item>>
				: T extends object
					? { readonly [Key in keyof T]: Localized<T[Key]> }
					: T;

const enterpriseTextEn = {
	metadata: {
		title: "Enterprise doteb - SSO, Audit Logs, Guardrails & Routing",
		description:
			"SOC 2 Type II compliant LLM infrastructure with SAML SSO, immutable audit logs, prompt-injection guardrails, per-project routing overrides, and white-label chat. Built for teams under SOC 2, HIPAA, or internal-AI scrutiny.",
		openGraph: {
			title: "Enterprise doteb",
			description:
				"SAML SSO, immutable audit logs, guardrails, per-project routing, and white-label chat - every capability a regulated team needs to put LLMs in production.",
		},
		twitter: {
			title: "Enterprise doteb",
			description:
				"SAML SSO, audit logs, guardrails, per-project routing, white-label chat. Production-grade LLM infrastructure for regulated teams.",
		},
	},
	hero: {
		badge: "ENTERPRISE",
		badgeDetail: "SOC 2 Type II certified",
		title: "Enterprise doteb for mission-critical applications",
		description:
			"Deploy a fully-managed or self-hosted LLM gateway with enterprise SSO, white-labeling, and infrastructure-as-code support for your cloud or bare metal infrastructure.",
		primaryCta: "Contact Us",
		secondaryCta: "Explore The Product",
		stats: {
			totalTokens: "Total Tokens Processed",
			totalRequests: "Total Requests",
			dailyTokens: "Daily Tokens",
			customerSavings: "Customer Savings",
		},
	},
	trustBar: {
		label: "Trusted by innovative teams worldwide",
	},
	security: {
		eyebrow: "Security & Compliance",
		title: "Your security review, already answered",
		description:
			"Prompts, completions, and API keys flow through your gateway - so the layer in the middle has to hold itself to a higher standard than the integrations it replaces. Ours is independently audited, and the evidence is one click away.",
		soc: {
			alt: "AICPA SOC for Service Organizations badge",
			status: "Audited",
			title: "SOC 2 Type II",
			description:
				"Independently audited against the AICPA Trust Services Criteria for security, availability, and confidentiality - controls verified in operation over a sustained period, not just on paper.",
			cta: "Request the full report",
		},
		gdpr: {
			status: "Compliant",
			title: "GDPR",
			description:
				"Data processing aligned with GDPR, with a DPA and subprocessor list ready for your legal team. Per-project routing can pin regulated workloads to EU regions to keep your compliance scope tight.",
			cta: "Read our privacy policy",
		},
		assurances: [
			{
				title: "Encrypted everywhere",
				description:
					"TLS in transit, AES-256 at rest - including provider keys.",
			},
			{
				title: "SSO upon request",
				description: "Okta, Azure AD, and Google with role-based permissions.",
			},
			{
				title: "Tamper-evident audit logs",
				description:
					"Every change - configuration, permissions, spend limits, key rotations.",
			},
			{
				title: "99.99% uptime SLA",
				description: "Backed by live status at status.doteb.com.",
			},
		],
	},
	support: {
		eyebrow: "Enterprise Support",
		title: "A direct line, not a ticket queue",
		description:
			"When LLM traffic is on your critical path, \"we'll get back to you in 2-3 business days\" isn't support. Every enterprise plan includes a shared channel with our engineering team.",
		cta: "Get your dedicated channel",
		channel: "acme-x-llmgateway",
		online: "Engineers online",
		footer: "A shared support channel in the tool your team already uses.",
		perks: [
			{
				title: "A dedicated support channel",
				description:
					"Your engineers and ours in one room, in the tool your team already uses. No ticket portal, no triage bot.",
			},
			{
				title: "Answers from the people who built it",
				description:
					"24/7 priority support staffed by gateway engineers - the same people who wrote the routing layer your traffic runs on.",
			},
			{
				title: "Hands-on onboarding and migration",
				description:
					"We help move your traffic, keys, and routing rules over - and stay in the channel until your team is fully ramped.",
			},
			{
				title: "Early access to new features",
				description:
					"Enterprise customers get new capabilities first, with a direct line to shape what we build next.",
			},
		],
		messages: [
			{
				initials: "SK",
				name: "Sarah",
				org: "Acme",
				time: "2:47 AM",
				avatarClass: "bg-rose-500/20 text-rose-400",
				text: "Seeing elevated p95 latency on our EU traffic for claude-sonnet requests - anything on your end?",
			},
			{
				initials: "LG",
				name: "Max",
				org: "doteb",
				time: "2:51 AM",
				avatarClass: "bg-blue-500/20 text-blue-400",
				badge: "4 min response",
				text: "On it. One upstream provider is degraded in eu-west - we've shifted your routing to the healthy fallback. p95 should normalize within a minute.",
			},
			{
				initials: "SK",
				name: "Sarah",
				org: "Acme",
				time: "2:53 AM",
				avatarClass: "bg-rose-500/20 text-rose-400",
				text: "Confirmed, dashboards look clean again.",
			},
		],
	},
	capabilities: {
		eyebrow: "Newly shipped",
		title: "Six capabilities your security team will actually approve",
		description:
			"The pieces that turn doteb from a developer tool into an auditable, multi-team, multi-tenant production platform. Each one ships with audit trails, SSO-aware permissions, and SIEM-ready exports.",
		deepDiveCta: "Read the deep-dive",
		footer:
			"Every capability ships under our enterprise plan - talk to us about scoping for your environment.",
		footerCta: "Book a 20-min walkthrough",
		featuresBySlug: {
			"audit-logs": {
				title: "Enterprise Audit Logs",
				tagline: "Trace any change back to a person, a minute, and an IP.",
				description:
					"Tamper-evident audit trails for SOC 2, HIPAA, ISO 27001, and internal investigations. Every config change, key rotation, and admin action - captured, attributed, exportable.",
			},
			"routing-overrides": {
				title: "Per-Project Routing Overrides",
				tagline:
					"Pin production to one region. Let staging fall back to anything.",
				description:
					"Override global routing rules at the project level - region, provider order, fallback chain, and cost ceilings. Production stays pinned; experimental teams stay flexible.",
			},
			guardrails: {
				title: "Enterprise Guardrails",
				tagline:
					"Run defense before inference - on every prompt, every project.",
				description:
					"Server-side detection for prompt injection, PII, secrets, and policy violations. Configured centrally, enforced at the gateway, auditable per-request.",
			},
			"discord-notifications": {
				title: "Webhook Alerts",
				tagline:
					"Send spend, error, and usage signals straight to the channel that owns them.",
				description:
					"Route incidents, budget thresholds, and usage milestones to the right operational channel with signed webhook payloads and dashboard deep links.",
			},
			"sso-saml": {
				title: "Single Sign-On (SAML / OIDC)",
				tagline: "Your IdP stays the source of truth for every login and role.",
				description:
					"Connect Okta, Azure AD, Google Workspace, or any SAML/OIDC provider. Map groups to roles, enforce SSO-only access, and keep off-boarding clean.",
			},
			"white-label": {
				title: "White-Label Chat & Playground",
				tagline: "Your logo, your domain, your colors - our infrastructure.",
				description:
					"Launch a branded internal AI workspace or customer-facing playground with custom domain, colors, model menus, and feature flags.",
			},
		},
	},
	features: {
		title: "Built for enterprise scale and security",
		description:
			"Everything you need to deploy and manage LLM infrastructure across your organization with confidence.",
		items: [
			{
				title: "Use Your Own API Keys",
				description:
					"Bring your own provider API keys without any surcharges. Full control over your costs.",
			},
			{
				title: "Lowest Fees on Credits",
				description:
					"Only 1% platform fee on credit purchases. Keep more of your budget for actual usage.",
			},
			{
				title: "Advanced Analytics",
				description:
					"Deep insights into usage patterns, costs, and performance across all your LLM operations.",
			},
			{
				title: "Unlimited Seats",
				description:
					"Add as many team members as you need. No per-seat pricing or user limits.",
			},
			{
				title: "On-boarding Assistance",
				description:
					"Dedicated support during setup and migration. We ensure a smooth transition for your team.",
			},
			{
				title: "Unlimited Data Retention",
				description:
					"Keep your request logs and analytics data forever. No automatic deletion or storage limits.",
			},
			{
				title: "24/7 Premium Support",
				description:
					"Round-the-clock access to our engineering team through a dedicated support channel. Priority response for any issues.",
			},
			{
				title: "Chat App & Whitelabel",
				description:
					"Full-featured chat playground included. Customize with your branding for internal or customer use.",
			},
			{
				title: "Single Sign-On (SSO)",
				description:
					"Seamless integration with your identity provider. Support for SAML, OAuth, and OIDC.",
			},
			{
				title: "Admin Dashboard",
				description:
					"Full-featured admin panel to manage organizations, monitor providers, track model performance, and control pricing.",
			},
			{
				title: "SOC 2 Type II Compliant",
				description:
					"doteb is SOC 2 Type II compliant, independently audited against the highest standards of security, availability, and confidentiality. Contact us to request the report.",
			},
		],
	},
	productShowcase: {
		eyebrow: "PLATFORM",
		eyebrowDetail: "Everything your team needs",
		title: "One platform for your entire LLM stack",
		description:
			"From analytics dashboards to chat playgrounds, image generation to developer docs - everything works together out of the box.",
		screenshots: [
			{
				alt: "doteb Dashboard",
				title: "Analytics Dashboard",
				description:
					"Real-time usage metrics, cost breakdowns, and performance monitoring across all your LLM operations.",
			},
			{
				alt: "doteb Chat Playground",
				title: "Chat Playground",
				description:
					"Interactive testing environment with model comparison, prompt engineering, and conversation management.",
			},
			{
				alt: "doteb Image Studio",
				title: "Image Studio",
				description:
					"Generate images with multiple providers and models. Compare outputs side-by-side with adjustable settings.",
			},
			{
				alt: "doteb Video Studio",
				title: "Video Studio",
				description:
					"Create AI-generated videos with Sora, Kling, and more. Set resolution, duration, and audio options from one interface.",
			},
			{
				alt: "doteb Admin Dashboard",
				title: "Admin Dashboard",
				description:
					"Full visibility into signups, revenue, provider health, and model performance across your deployment.",
			},
			{
				alt: "doteb Documentation",
				title: "Developer Documentation",
				description:
					"Comprehensive API reference, integration guides, and self-hosting documentation for your team.",
			},
		],
		whiteLabel: {
			title: "Fully white-labelable",
			description:
				"Replace the doteb logo and branding with your own. Every dashboard, playground, and docs page can be customized to match your company identity.",
		},
	},
	adminDashboard: {
		eyebrow: "SELF-HOSTED",
		eyebrowDetail: "Included with every enterprise deployment",
		title: "A full admin dashboard, on your infrastructure",
		description:
			"When you self-host doteb, you get a complete admin dashboard to monitor, manage, and optimize your entire LLM operation. No external dependencies, no data leaving your network.",
		requestDemo: "Request a demo",
		capabilities: [
			{
				title: "Real-Time Metrics",
				description:
					"Track signups, revenue, verified users, and paying customers from a single view. Configurable time ranges from 7 days to all-time.",
			},
			{
				title: "Organization Management",
				description:
					"Search, sort, and drill into every organization. View projects, API keys, transactions, credits, and spending at a glance.",
			},
			{
				title: "Provider Monitoring",
				description:
					"Monitor every LLM provider in real time. Track request volume, error rates, cache hit ratios, and average time to first token.",
			},
			{
				title: "Model Performance",
				description:
					"See which models are being used, how often they fail, and how fast they respond. Filter and sort across hundreds of models instantly.",
			},
			{
				title: "Discount Controls",
				description:
					"Set global or per-organization discounts by provider and model. Full control over pricing without touching code.",
			},
			{
				title: "Revenue Analytics",
				description:
					"Interactive charts for signup trends and revenue over time. Spot patterns, track growth, and forecast with confidence.",
			},
		],
	},
	pricing: {
		title: "Enterprise pricing that scales with you",
		description:
			"Choose between self-hosted control or fully managed convenience. Both options include all enterprise features.",
		contactNote: "Contact us for custom plans and enterprise agreements.",
		plans: [
			{
				name: "Self-Hosted",
				description: "Deploy on your infrastructure with complete control",
				features: [
					"Full admin dashboard included",
					"Enterprise SSO integration",
					"Provider configuration UI",
					"Terraform modules for AWS, GCP, bare metal",
					"White label gateway & chat playground",
					"Prioritized feature requests",
					"On-boarding assistance",
					"Dedicated support channel",
				],
				cta: "Get In Touch",
				highlighted: false,
			},
			{
				name: "Enterprise Cloud",
				description: "Fully managed with custom scaling and pricing",
				features: [
					"Everything in Self-Hosted",
					"Fully managed infrastructure",
					"Custom rate limits",
					"Volume-based pricing",
					"Advanced monitoring & analytics",
					"99.9% SLA guarantee",
					"Priority incident response",
				],
				cta: "Contact Us",
				highlighted: true,
			},
		],
	},
	contact: {
		title: "Get Started with Enterprise",
		description:
			"Tell us about your needs and our team will reach out to discuss how doteb can support your organization.",
		successTitle: "Thank you for reaching out!",
		successDescription:
			"We've received your message. Book a time below and our team will meet you then - otherwise we'll reply within 24 hours.",
		sendAnother: "Send Another Message",
		requiredFields: "Required fields",
		submit: "Submit Request",
		submitting: "Sending...",
		honeypotLabel: "Leave this field empty",
		fields: {
			name: "Name",
			email: "Company Email",
			country: "Country",
			size: "Company Size",
			message: "How can we help?",
		},
		placeholders: {
			name: "John Doe",
			email: "john@company.com",
			country: "Select country",
			size: "Select size",
			message:
				"Tell us about your use case, requirements, or any questions you have...",
		},
		companySizes: [
			"1-50 employees",
			"51-200 employees",
			"201-500 employees",
			"501-1000 employees",
			"1000+ employees",
		],
		validation: {
			name: "Name must be at least 2 characters",
			email: "Invalid email address",
			country: "Please select a country",
			size: "Please select company size",
			message: "Message must be at least 10 characters",
		},
		toast: {
			successTitle: "Message sent successfully!",
			successDescription: "Our team will reach out to you shortly.",
			errorTitle: "Failed to send message",
			errorDescription: "Please try again later.",
			unknownTitle: "Something went wrong",
			unknownDescription: "Please try again later or contact us directly.",
		},
		calendly: {
			preferNewTab: "Prefer a new tab?",
			openScheduler: "Open the scheduler",
		},
	},
	uptime: {
		eyebrow: "RELIABILITY",
		titlePrefix: "Never go down.",
		titleMuted: "Even when your providers do.",
		description:
			"doteb automatically routes requests to healthy providers in real-time. When one goes down, your traffic seamlessly fails over - your users never notice.",
		ariaLabel:
			"Visualization showing individual provider uptime at 94% each, combining to 99.9999% through automatic failover",
		failoverLabel: "Automatic failover",
		withoutLabel: "WITHOUT LLM GATEWAY",
		withLabel: "WITH LLM GATEWAY",
		providerUptime: "uptime per provider",
		withoutDowntime: "of downtime per year",
		combinedUptime: "combined uptime across providers",
		withDowntime: "of downtime per year",
		math: "Each provider averages ~94% uptime independently. With automatic failover across multiple providers, the probability of simultaneous downtime drops to near zero - giving you effective uptime of 99.9999%.",
	},
} as const;

const enterpriseTextZh = {
	metadata: {
		title: "企业级 doteb - SSO、审计日志、护栏与路由",
		description:
			"符合 SOC 2 Type II 的 LLM 基础设施，提供 SAML SSO、不可篡改审计日志、提示注入护栏、按项目路由覆盖和白标聊天。面向受 SOC 2、HIPAA 或内部 AI 审查约束的团队构建。",
		openGraph: {
			title: "企业级 doteb",
			description:
				"SAML SSO、不可篡改审计日志、护栏、按项目路由和白标聊天 - 受监管团队把 LLM 投产所需的能力都在这里。",
		},
		twitter: {
			title: "企业级 doteb",
			description:
				"SAML SSO、审计日志、护栏、按项目路由、白标聊天。面向受监管团队的生产级 LLM 基础设施。",
		},
	},
	hero: {
		badge: "企业版",
		badgeDetail: "SOC 2 Type II 认证",
		title: "面向关键业务应用的企业级 doteb",
		description:
			"部署全托管或自托管 LLM gateway，获得企业 SSO、白标化和基础设施即代码支持，可运行在你的云环境或裸金属基础设施上。",
		primaryCta: "联系我们",
		secondaryCta: "了解产品",
		stats: {
			totalTokens: "累计处理 Tokens",
			totalRequests: "累计请求数",
			dailyTokens: "每日 Tokens",
			customerSavings: "客户节省成本",
		},
	},
	trustBar: {
		label: "受到全球创新团队信任",
	},
	security: {
		eyebrow: "安全与合规",
		title: "你的安全审查问题，已经有答案",
		description:
			"提示词、补全结果和 API keys 都会流经你的 gateway，因此中间层必须比它替代的集成承担更高标准。我们的控制已通过独立审计，证据一键可取。",
		soc: {
			alt: "AICPA SOC for Service Organizations 徽章",
			status: "已审计",
			title: "SOC 2 Type II",
			description:
				"围绕安全性、可用性和机密性，按 AICPA Trust Services Criteria 完成独立审计；控制项经过持续运行验证，而不只是停留在文档上。",
			cta: "申请完整报告",
		},
		gdpr: {
			status: "已合规",
			title: "GDPR",
			description:
				"数据处理与 GDPR 对齐，并为你的法务团队准备好 DPA 和子处理方清单。按项目路由可将受监管工作负载固定到 EU 区域，收紧合规边界。",
			cta: "阅读隐私政策",
		},
		assurances: [
			{
				title: "全链路加密",
				description:
					"传输中使用 TLS，静态数据使用 AES-256，包括 provider keys。",
			},
			{
				title: "按需启用 SSO",
				description: "支持 Okta、Azure AD 和 Google，并配套基于角色的权限。",
			},
			{
				title: "防篡改审计日志",
				description: "记录配置、权限、花费限制、key 轮换等每一次变更。",
			},
			{
				title: "99.99% uptime SLA",
				description: "由 status.doteb.com 的实时状态支撑。",
			},
		],
	},
	support: {
		eyebrow: "企业支持",
		title: "直达工程团队，而不是排队工单",
		description:
			"当 LLM 流量位于关键路径上，“2-3 个工作日内回复”不能算支持。每个企业版方案都包含与你团队和我们工程团队共用的支持频道。",
		cta: "创建你的专属频道",
		channel: "acme-x-llmgateway",
		online: "工程师在线",
		footer: "在你的团队已使用工具中创建共享支持频道。",
		perks: [
			{
				title: "专属支持频道",
				description:
					"你的工程师和我们的工程师在同一个房间，使用团队已经熟悉的工具。没有工单门户，也没有分流机器人。",
			},
			{
				title: "由产品构建者直接答复",
				description:
					"24/7 优先支持由 gateway 工程师提供，也就是编写你流量所运行路由层的人。",
			},
			{
				title: "上手和迁移全程协助",
				description:
					"我们协助迁移流量、keys 和路由规则，并持续留在频道里，直到你的团队完全跑顺。",
			},
			{
				title: "新功能优先体验",
				description:
					"企业客户会优先获得新能力，并能直接影响我们接下来构建什么。",
			},
		],
		messages: [
			{
				initials: "SK",
				name: "Sarah",
				org: "Acme",
				time: "2:47 AM",
				avatarClass: "bg-rose-500/20 text-rose-400",
				text: "我们 EU 流量里的 claude-sonnet 请求 p95 延迟升高，你们这边有异常吗？",
			},
			{
				initials: "LG",
				name: "Max",
				org: "doteb",
				time: "2:51 AM",
				avatarClass: "bg-blue-500/20 text-blue-400",
				badge: "4 分钟响应",
				text: "正在处理。eu-west 有一个上游 provider 降级，我们已经把你的路由切到健康的 fallback。p95 应该会在一分钟内恢复。",
			},
			{
				initials: "SK",
				name: "Sarah",
				org: "Acme",
				time: "2:53 AM",
				avatarClass: "bg-rose-500/20 text-rose-400",
				text: "确认，仪表盘已经恢复正常。",
			},
		],
	},
	capabilities: {
		eyebrow: "最新上线",
		title: "六项你的安全团队真正会批准的能力",
		description:
			"这些能力会把 doteb 从开发工具变成可审计、多团队、多租户的生产平台。每一项都内置审计轨迹、感知 SSO 的权限和可接入 SIEM 的导出。",
		deepDiveCta: "阅读深度说明",
		footer: "所有能力都包含在企业版方案中 - 联系我们一起评估你的环境范围。",
		footerCta: "预约 20 分钟演示",
		featuresBySlug: {
			"audit-logs": {
				title: "企业审计日志",
				tagline: "把每次变更追溯到人、时间和 IP。",
				description:
					"面向 SOC 2、HIPAA、ISO 27001 和内部调查的防篡改审计轨迹。每次配置变更、key 轮换和管理员操作都会被捕获、归因并可导出。",
			},
			"routing-overrides": {
				title: "按项目路由覆盖",
				tagline: "生产固定到一个区域，测试环境可自由 fallback。",
				description:
					"在项目级覆盖全局路由规则，包括区域、provider 顺序、fallback 链和成本上限。生产保持固定，实验团队保持灵活。",
			},
			guardrails: {
				title: "企业级护栏",
				tagline: "每个 prompt、每个项目，都先防御再推理。",
				description:
					"服务端检测提示注入、PII、密钥和策略违规。集中配置，在 gateway 执行，并对每个请求保持可审计。",
			},
			"discord-notifications": {
				title: "Webhook 告警",
				tagline: "把花费、错误和用量信号直接送到负责它们的频道。",
				description:
					"将事故、预算阈值和用量里程碑路由到正确的运营频道，并附带签名 webhook payload 和仪表盘深链。",
			},
			"sso-saml": {
				title: "单点登录 (SAML / OIDC)",
				tagline: "你的 IdP 继续作为登录和角色的唯一事实源。",
				description:
					"连接 Okta、Azure AD、Google Workspace 或任意 SAML/OIDC provider。映射组到角色、强制仅 SSO 登录，并保持离职下线干净。",
			},
			"white-label": {
				title: "白标聊天与 Playground",
				tagline: "你的 logo、你的域名、你的配色 - 我们的基础设施。",
				description:
					"发布带品牌的内部 AI 工作区或面向客户的 playground，支持自定义域名、颜色、模型菜单和功能开关。",
			},
		},
	},
	features: {
		title: "为企业规模和安全性构建",
		description:
			"跨组织部署和管理 LLM 基础设施所需的一切能力，都可以有信心地使用。",
		items: [
			{
				title: "使用你自己的 API Keys",
				description: "自带 provider API keys，不收额外加价。完整掌控你的成本。",
			},
			{
				title: "Credits 最低费率",
				description: "购买 credits 仅收 1% 平台费，让更多预算用于真实调用。",
			},
			{
				title: "高级分析",
				description: "深入了解所有 LLM 操作中的用量模式、成本和性能表现。",
			},
			{
				title: "不限席位",
				description: "按需添加团队成员。没有按席位计费，也没有用户数限制。",
			},
			{
				title: "上手协助",
				description: "设置和迁移期间提供专属支持，确保你的团队顺利完成切换。",
			},
			{
				title: "无限数据保留",
				description:
					"永久保留请求日志和分析数据。没有自动删除，也没有存储上限。",
			},
			{
				title: "24/7 高级支持",
				description:
					"通过专属支持频道全天候联系工程团队，任何问题都享有优先响应。",
			},
			{
				title: "聊天应用与白标",
				description:
					"内置完整聊天 playground。可按你的品牌定制，供内部或客户使用。",
			},
			{
				title: "单点登录 (SSO)",
				description: "无缝集成你的身份提供方。支持 SAML、OAuth 和 OIDC。",
			},
			{
				title: "Admin Dashboard",
				description:
					"完整管理面板，用于管理组织、监控 providers、跟踪模型性能并控制定价。",
			},
			{
				title: "SOC 2 Type II 合规",
				description:
					"doteb 符合 SOC 2 Type II，并围绕安全性、可用性和机密性完成独立审计。可联系我们申请报告。",
			},
		],
	},
	productShowcase: {
		eyebrow: "平台",
		eyebrowDetail: "团队所需能力一站齐备",
		title: "覆盖整个 LLM 技术栈的一体化平台",
		description:
			"从分析仪表盘到聊天 playground，从图像生成到开发者文档，所有能力开箱即用、协同工作。",
		screenshots: [
			{
				alt: "doteb 仪表盘",
				title: "分析仪表盘",
				description:
					"实时用量指标、成本拆解和性能监控，覆盖你的所有 LLM 操作。",
			},
			{
				alt: "doteb 聊天 Playground",
				title: "Chat Playground",
				description: "交互式测试环境，支持模型对比、prompt 工程和对话管理。",
			},
			{
				alt: "doteb 图像工作室",
				title: "Image Studio",
				description:
					"使用多个 providers 和模型生成图像，并通过可调参数并排比较输出。",
			},
			{
				alt: "doteb 视频工作室",
				title: "Video Studio",
				description:
					"通过 Sora、Kling 等创建 AI 生成视频，在一个界面里设置分辨率、时长和音频选项。",
			},
			{
				alt: "doteb Admin Dashboard",
				title: "Admin Dashboard",
				description:
					"完整查看注册、收入、provider 健康状态和整个部署中的模型性能。",
			},
			{
				alt: "doteb 文档",
				title: "开发者文档",
				description: "为团队提供完整 API 参考、集成指南和自托管文档。",
			},
		],
		whiteLabel: {
			title: "完全可白标",
			description:
				"将 doteb 的 logo 和品牌替换为你自己的。每个仪表盘、playground 和文档页面都可以定制，以匹配你的公司身份。",
		},
	},
	adminDashboard: {
		eyebrow: "自托管",
		eyebrowDetail: "每个企业部署都包含",
		title: "运行在你基础设施上的完整 Admin Dashboard",
		description:
			"自托管 doteb 时，你会获得完整 admin dashboard，用于监控、管理并优化整个 LLM 运营。没有外部依赖，也没有数据离开你的网络。",
		requestDemo: "申请演示",
		capabilities: [
			{
				title: "实时指标",
				description:
					"在一个视图里跟踪注册、收入、已验证用户和付费客户。时间范围可从 7 天配置到全量历史。",
			},
			{
				title: "组织管理",
				description:
					"搜索、排序并深入查看每个组织。一眼查看项目、API keys、交易、credits 和花费。",
			},
			{
				title: "Provider 监控",
				description:
					"实时监控每个 LLM provider。跟踪请求量、错误率、缓存命中率和平均首 token 时间。",
			},
			{
				title: "模型性能",
				description:
					"查看哪些模型被使用、失败频率和响应速度。可即时筛选和排序数百个模型。",
			},
			{
				title: "折扣控制",
				description:
					"按全局或组织级为 provider 和模型设置折扣。不改代码即可完整控制定价。",
			},
			{
				title: "收入分析",
				description:
					"用交互式图表查看注册趋势和收入变化。识别模式、跟踪增长，并更有信心地预测。",
			},
		],
	},
	pricing: {
		title: "随你扩展的企业定价",
		description:
			"在自托管控制力和全托管便利性之间选择。两种方案都包含所有企业能力。",
		contactNote: "如需自定义方案和企业协议，请联系我们。",
		plans: [
			{
				name: "Self-Hosted",
				description: "部署在你的基础设施上，获得完整控制权",
				features: [
					"包含完整 admin dashboard",
					"企业 SSO 集成",
					"Provider 配置 UI",
					"面向 AWS、GCP、裸金属的 Terraform modules",
					"白标 gateway 与聊天 playground",
					"优先功能请求",
					"上手协助",
					"专属支持频道",
				],
				cta: "联系我们",
				highlighted: false,
			},
			{
				name: "Enterprise Cloud",
				description: "全托管，并支持自定义扩展和定价",
				features: [
					"包含 Self-Hosted 的全部能力",
					"全托管基础设施",
					"自定义速率限制",
					"基于用量的定价",
					"高级监控与分析",
					"99.9% SLA 保证",
					"优先事故响应",
				],
				cta: "联系我们",
				highlighted: true,
			},
		],
	},
	contact: {
		title: "开始使用企业版",
		description:
			"告诉我们你的需求，团队会联系你，讨论 doteb 如何支持你的组织。",
		successTitle: "感谢你的联系！",
		successDescription:
			"我们已收到你的消息。你可以在下方预约时间，我们的团队会准时沟通；否则我们会在 24 小时内回复。",
		sendAnother: "再发送一条消息",
		requiredFields: "必填字段",
		submit: "提交请求",
		submitting: "发送中...",
		honeypotLabel: "请留空此字段",
		fields: {
			name: "姓名",
			email: "公司邮箱",
			country: "国家或地区",
			size: "公司规模",
			message: "我们可以如何帮助你？",
		},
		placeholders: {
			name: "张三",
			email: "zhang@company.com",
			country: "选择国家或地区",
			size: "选择规模",
			message: "请告诉我们你的使用场景、需求或问题...",
		},
		companySizes: [
			"1-50 人",
			"51-200 人",
			"201-500 人",
			"501-1000 人",
			"1000+ 人",
		],
		validation: {
			name: "姓名至少需要 2 个字符",
			email: "邮箱地址无效",
			country: "请选择国家或地区",
			size: "请选择公司规模",
			message: "消息至少需要 10 个字符",
		},
		toast: {
			successTitle: "消息已发送！",
			successDescription: "我们的团队会尽快联系你。",
			errorTitle: "消息发送失败",
			errorDescription: "请稍后重试。",
			unknownTitle: "出现了一些问题",
			unknownDescription: "请稍后重试，或直接联系我们。",
		},
		calendly: {
			preferNewTab: "想在新标签页打开？",
			openScheduler: "打开预约页面",
		},
	},
	uptime: {
		eyebrow: "可靠性",
		titlePrefix: "服务不要宕机。",
		titleMuted: "即使你的 providers 出了问题。",
		description:
			"doteb 会实时将请求路由到健康的 providers。当某个 provider 不可用时，流量会无感 failover，用户不会察觉。",
		ariaLabel:
			"可视化展示单个 provider 的 uptime 为 94%，通过自动 failover 组合达到 99.9999%",
		failoverLabel: "自动 failover",
		withoutLabel: "不使用 LLM GATEWAY",
		withLabel: "使用 LLM GATEWAY",
		providerUptime: "单个 provider uptime",
		withoutDowntime: "每年停机时间",
		combinedUptime: "跨 providers 的综合 uptime",
		withDowntime: "每年停机时间",
		math: "每个 provider 独立平均约 94% uptime。通过跨多个 providers 自动 failover，同时停机的概率会接近于零，从而获得 99.9999% 的有效 uptime。",
	},
} satisfies Localized<typeof enterpriseTextEn>;

const enterpriseText = {
	en: enterpriseTextEn,
	"zh-CN": enterpriseTextZh,
};

export function selectEnterpriseText(
	locale: string | undefined,
): Localized<typeof enterpriseTextEn> {
	return locale === "zh-CN" ? enterpriseText["zh-CN"] : enterpriseText.en;
}
